import { useUser } from "@clerk/clerk-react";
import { useToken } from "@gluestack-style/react";
import Section from "components/general/Section";
import { Box, Button, H1, H2, H3, Typography } from "components/ui";
import { Formik } from "formik";
import React, { useEffect, useState } from "react";
import { ActivityIndicator } from "react-native";
import Animated, { Easing, useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";
import { usePostUserSetupMutation } from "store/abcoApi";
import * as Yup from "yup";
import FormField from "./FormField";

interface SetupUserFormProps {}

export default function SetupUserForm({}: SetupUserFormProps) {
	const { isLoaded, user } = useUser();
	const [hasSubmitted, setHasSubmitted] = React.useState(false);
	const [messageIndex, setMessageIndex] = useState(0);
	const opacity = useSharedValue(1);

	const messages = [
		"Setting up your account...",
		"Abco uses our own employed Delivery drivers for all metros areas in metro areas in Australia and New Zealand.",
		"e-Riteoff (171101) is an Environmentally Friendly, Biodegradable gum, mark, and Graffiti Remover.",
		"Enviroplus solutions have received a platinum health rating, making them the first concentrated, high-performing, safest commercial cleaning products on the market!",
		"Enviroplus solutions have received a platinum health rating, making them the first concentrated, high-performing, safest commercial cleaning products on the market!",
		"Did you know Abco has its in-house servicing team?",
		,
		"Abco has over 20,000 m2 of warehouse strategically located throughout Australia and New Zealand.",
		,
	];

	const animatedStyle = useAnimatedStyle(() => {
		return {
			opacity: opacity.value,
			minWidth: 500,
			alignItems: "center",
			marginTop: 16,
		};
	});

	useEffect(() => {
		const interval = setInterval(() => {
			// Fade out
			opacity.value = withTiming(
				0,
				{
					duration: 500,
					easing: Easing.out(Easing.ease),
				},
				() => {
					// Change message and fade back in
					setMessageIndex((prevIndex) => (prevIndex + 1) % messages.length);
					opacity.value = withTiming(1, {
						duration: 500,
						easing: Easing.in(Easing.ease),
					});
				},
			);
		}, 5000);

		return () => clearInterval(interval);
	}, []);

	const resolvedErrorBright = useToken("colors", "errorbright");
	const validationSchema = Yup.object().shape({
		companyName: Yup.string().required("Company name is required").min(3, "Company name must be at least 3 characters"),
	});

	const [postSetup, { isLoading: isSaving, isError }] = usePostUserSetupMutation();

	return (
		// @ts-ignore
		<Box alignItems="center" justifyContent="center" style={{ height: "100vh" }} gap={20}>
			{isError && (
				<Section alignItems="center" gap={12}>
					<H2>Oops! Something went wrong</H2>
					<Typography color="$neutral600">Sorry for the inconvenience. Please try again later.</Typography>
					{/* <Box minHeight={150}>
					<ActivityIndicator size={"large"} style={{ transform: [{ scaleX: 2 }, { scaleY: 2 }, { translateY: 25 }] }} />
				</Box> */}
				</Section>
			)}
			{isSaving && (
				<Section maxWidth={752}>
					<Animated.View style={animatedStyle}>
						<H3 style={{ textAlign: "center" }}>{messages[messageIndex]}</H3>
					</Animated.View>

					<Box minHeight={150}>
						<ActivityIndicator size={"large"} style={{ transform: [{ scaleX: 2 }, { scaleY: 2 }, { translateY: 25 }] }} />
					</Box>
				</Section>
			)}

			{!hasSubmitted && (
				<>
					<Section py={12} bg="transparent">
						<H1>Welcome to Abco</H1>
					</Section>
					<Section gap={20}>
						<Box gap={6}>
							<H3>Setup your account</H3>
							<Typography.ParagraphSmall color="$neutral600">Please enter the name of your company. This will be used to identify your account and for billing purposes.</Typography.ParagraphSmall>
						</Box>
						<Formik
							initialValues={{
								companyName: "",
							}}
							validationSchema={validationSchema}
							onSubmit={async (values) => {
								setHasSubmitted(true);
								postSetup({ userSetup: values }).then((res) => {
									user?.reload();
								});
							}}
						>
							{(formik) => (
								<Box gap={20}>
									<FormField type="input" label="Company Name" name="companyName" placeholder="ABC Cleaning Co" formik={formik} />
									<Button mt={20} variant="secondary" onPress={() => formik.handleSubmit()}>
										<Typography weight="bold">Continue</Typography>
									</Button>
								</Box>
							)}
						</Formik>
					</Section>
				</>
			)}
		</Box>
	);
}
